/**
 * SSE Notification Service
 * Handles Server-Sent Events connection for real-time notifications
 */

class SSENotificationService {
  constructor() {
    this.eventSource = null
    this.reconnectInterval = 5000
    this.reconnectTimer = null
    this.listeners = []
    this.isConnected = false
    this.reconnectAttempts = 0
    this.maxReconnectAttempts = 10
  }

  /**
   * Connect to SSE endpoint
   * @param {string} url - The SSE stream endpoint URL
   * @param {string} token - Authentication token
   */
  connect(url, token) {
    if (this.eventSource) {
      this.disconnect()
    }

    try {
      // Append token as query parameter for authentication
      const streamUrl = `${url}?token=${token}`
      
      this.eventSource = new EventSource(streamUrl)
      
      this.eventSource.onopen = () => {
        console.log('SSE connection established')
        this.isConnected = true
        this.reconnectAttempts = 0
        this.notifyListeners({ type: 'connected', data: null })
      }
      
      this.eventSource.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data)
          console.log('SSE message received:', data)
          this.notifyListeners({ type: 'message', data })
        } catch (error) {
          console.error('Error parsing SSE message:', error)
        }
      }
      
      this.eventSource.onerror = (error) => {
        console.error('SSE connection error:', error)
        this.isConnected = false
        this.notifyListeners({ type: 'error', data: error })
        this.handleReconnect(url, token)
      }
    } catch (error) {
      console.error('Error establishing SSE connection:', error)
      this.handleReconnect(url, token)
    }
  }

  /**
   * Handle reconnection logic
   */
  handleReconnect(url, token) {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer)
    }

    if (this.reconnectAttempts < this.maxReconnectAttempts) {
      this.reconnectAttempts++
      console.log(`Attempting to reconnect (${this.reconnectAttempts}/${this.maxReconnectAttempts})...`)
      
      this.reconnectTimer = setTimeout(() => {
        this.connect(url, token)
      }, this.reconnectInterval)
    } else {
      console.error('Max reconnection attempts reached')
      this.notifyListeners({ type: 'maxReconnectReached', data: null })
    }
  }

  /**
   * Disconnect from SSE
   */
  disconnect() {
    if (this.eventSource) {
      this.eventSource.close()
      this.eventSource = null
      this.isConnected = false
      console.log('SSE connection closed')
    }
    
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer)
      this.reconnectTimer = null
    }
  }

  /**
   * Add event listener
   * @param {Function} callback - Callback function to handle events
   */
  addListener(callback) {
    this.listeners.push(callback)
  }

  /**
   * Remove event listener
   * @param {Function} callback - Callback function to remove
   */
  removeListener(callback) {
    this.listeners = this.listeners.filter(listener => listener !== callback)
  }

  /**
   * Notify all listeners
   * @param {Object} event - Event object
   */
  notifyListeners(event) {
    this.listeners.forEach(listener => {
      try {
        listener(event)
      } catch (error) {
        console.error('Error in SSE listener:', error)
      }
    })
  }

  /**
   * Check if connected
   * @returns {boolean}
   */
  getConnectionStatus() {
    return this.isConnected
  }
}

export default new SSENotificationService()
